import { Loader2 } from "lucide-react";
import { useState } from "react";
import { toast } from "sonner";

import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { type ActionResult, createTeamAction } from "@/features/teams/actions";

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /**
   * Called after a successful create. The parent typically closes the dialog
   * and `router.refresh()`es so the new team's empty state lights up.
   */
  onCreated?: (result: Extract<ActionResult, { ok: true }>) => void;
}

/**
 * Modal for creating a new team.
 *
 * Sits inside the same Org as the caller's current active team (the backend
 * scopes by `ctx.org_id`). The caller is auto-promoted to `team_admin` and
 * the active team is rotated to the new one — same kos_token rotation
 * pattern as `/v1/me/active-team`.
 */
export function CreateTeamDialog({ open, onOpenChange, onCreated }: Props) {
  const [name, setName] = useState("");
  const [pending, setPending] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const submit = async (): Promise<void> => {
    setError(null);
    if (!name.trim()) {
      setError("Team name must not be empty");
      return;
    }
    setPending(true);
    try {
      const result = await createTeamAction(name);
      if (result.ok) {
        toast.success(result.message);
        setName("");
        onCreated?.(result);
      } else {
        setError(result.message);
      }
    } finally {
      setPending(false);
    }
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (!pending) onOpenChange(next);
      }}
    >
      <DialogContent className="sm:max-w-[420px]">
        <DialogHeader>
          <DialogTitle>Create a new team</DialogTitle>
          <DialogDescription>
            A new workspace inside your current organization. You'll be added as an admin and
            switched into it immediately.
          </DialogDescription>
        </DialogHeader>
        <form
          onSubmit={(e) => {
            e.preventDefault();
            void submit();
          }}
          className="space-y-3"
        >
          <div className="space-y-1.5">
            <Label htmlFor="team-name">Team name</Label>
            <Input
              id="team-name"
              autoFocus
              maxLength={200}
              placeholder="e.g. Skunkworks"
              value={name}
              onChange={(e) => setName(e.target.value)}
              disabled={pending}
            />
            {error ? <p className="text-[12px] text-destructive">{error}</p> : null}
          </div>
          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              disabled={pending}
            >
              Cancel
            </Button>
            <Button type="submit" disabled={pending || !name.trim()}>
              {pending ? <Loader2 className="mr-1 h-3.5 w-3.5 animate-spin" /> : null}
              Create team
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
